<template>
  <v-stepper alt-labels>
    <v-stepper-header>
      <v-stepper-step
        :complete="false"
        step="1"
      >
        Cart
      </v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step step="2">
        Billing
      </v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step step="3">
        Review
      </v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step step="4">
        Complete
      </v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <v-stepper-content step="1">
        <h4>Order Information {{ organizationDetails && organizationDetails.name ? `for ${organizationDetails.name}` : ''}}</h4>
        <div class="text-h6 mb-4 text-right ">Total: {{ util.formatPrice(getTotal()) }}</div>
        <v-alert
          v-if="showCotermDurationWarning"
          prominent
          type="warning"
        >
          The organization {{ organizationDetails && organizationDetails.name ? organizationDetails.name : ''}} uses Meraki Co-Termination licensing.
          Selecting different duration options for devices in such organization may cause unexpected issues. Please refer to
          <a
            href="https://documentation.meraki.com/General_Administration/Licensing/Meraki_Co-Termination_Licensing_Overview"
            target="_blank"
          >
            Meraki Documentation
          </a>
          to learn more.
        </v-alert>
        <v-row class="license-cart mb-6">
          <v-col cols="12" md="12" class="py-0">
            <v-card class="lightgray">
              <v-card-text class="pl-3 pr-3">
                <v-data-table
                  :headers="headers"
                  :items="data"
                  :items-per-page="rowsPerPage"
                  fixed-header
                  height="400"
                  hide-default-footer
                  must-sort
                  class="lightgray"
                  @update:sort-by="sortBy"
                  @update:sort-desc="changeSortDesc"
                >
                  <template v-slot:item.current_license_type="{item}">
                    <span v-if="!item.current_license_type">
                      -
                    </span>
                    <span v-if="item.current_license_type">
                      {{item.current_license_type}}
                    </span>
                  </template>
                  <template v-slot:header.price="{ header }">
                    Price HT
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ icons.mdiInformation }}
                        </v-icon>
                      </template>
                      <span>Plus applicable taxes. Taxes will be calculated in the next step.</span>
                    </v-tooltip>
                  </template>
                  <template v-slot:header.totalPrice="{ header }">
                    Total Price
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ icons.mdiInformation }}
                        </v-icon>
                      </template>
                      <span>Plus applicable taxes. Taxes will be calculated in the next step.</span>
                    </v-tooltip>
                  </template>
                  <template v-slot:item.selectedLicenseEdition="{item}">
                    <v-select
                      :disabled="!item.valid_license_combinations || (item.valid_license_combinations && item.valid_license_combinations.length === 0)
                       || !item.licenseEditionOptions || (item.licenseEditionOptions && item.licenseEditionOptions.length === 0)"
                      :items="item.licenseEditionOptions"
                      @change="(event) => handleLicenseEditionChange(event, item)"
                      v-model="item.selectedLicenseEdition"
                      style="max-width: 200px;"
                      hide-details="auto"
                      label=""
                    >
                    </v-select>
                  </template>
                  <template v-slot:item.selectedDuration="{item}">
                    <v-select
                      :disabled="!item.durationOptions || (item.durationOptions && item.durationOptions.length === 0)"
                      :items="item.durationOptions"
                      @change="(event) => handleDurationChange(event, item)"
                      v-model="item.selectedDuration"
                      style="max-width: 120px;"
                      hide-details="auto"
                      label=""
                    ></v-select>
                  </template>
                  <template v-slot:item.selectedLicense="{item}">
                    {{ item.selectedLicense ? item.selectedLicense : '' }}
                    <v-alert v-if="!item.selectedLicense" class="ma-0 pt-1 pb-1 pr-1 pl-2" style="font-size: 10px; width: 160px;"
                      type="warning" dense outlined text
                    >No License found</v-alert>
                  </template>
                  <template v-slot:item.price="{item}">
                    {{ util.formatPrice(item.price) }}
                  </template>
                  <template v-slot:item.price="{item}">
                    {{ util.formatPrice(item.price) }}
                  </template>
                  <template v-slot:item.totalPrice="{item}">
                    {{ util.formatPrice(item.price * item.quantity) }}
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <v-text-field
                      style="max-width: 120px;"
                      :rules="getQtyRules(item)"
                      v-model="item.quantity"
                      single-line
                      hide-details="auto"
                      type="number"
                    />
                  </template>
                  <template v-slot:item.action="{item}">
                    <v-btn icon depressed small @click="() => removeItemFromCart(item)"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>

                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <pagination
              v-model="currentPage"
              :total-records="totalRecords"
              @load-data="loadData"
              @set-rows-per-page="setRowsPerPage"
            />
          </v-col>
        </v-row>

        <h4>Organization Information</h4>
        <v-row class="mb-10">
          <v-col cols="12" md="12" class="py-0">
            <v-card class="lightgray">
              <v-card-text class="pl-3 pr-3">
                <GoogleAddressForm v-model="organizationAddressInfo" :errors="errors"></GoogleAddressForm>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>


        <div style="margin-top: -20px;" class="text-small">
          <sup>*</sup>This service is only available for US-based customers.
        </div>

        <v-checkbox
          class="pa-4"
          v-model="agreedToDisclaimer"
          :rules="[v => !!v || 'You must agree to continue!']"
          :error="errors.agreedToDisclaimer !== null"
          :error-messages="errors.agreedToDisclaimer"
          label="I agree with order information being sent to Rhino Networks, a third-party provider that will be in charge of managing the transaction."
          required
        ></v-checkbox>

        <v-alert
          v-if="urlError"
          type="error"
        >{{urlError}}</v-alert>

        <div>
          <v-btn
            class="mr-4"
            color="primary"
            @click="continueToBillingInformation()"
          >
            Continue to Billing Information
          </v-btn>

          <v-btn text to="license-management">
            Cancel
          </v-btn>
        </div>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import {mapActions} from 'vuex';

import util from '@/util';
import {sendGet, sendPost} from '@/util/api';
import GoogleAddressForm from '@/components/GoogleAddressForm';
import Pagination from '@/components/Pagination';
import {mdiInformation,} from '@mdi/js';

export default {
  components: {
    GoogleAddressForm,
    Pagination,
  },

  props: {
    orgId: {
      type: String,
    },
    deviceIds: {
      type: Array,
      default: () => [],
    },
    expiredOnly: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      icons: {
        mdiInformation,
      },
      headers: [
        { text: 'Device Model', value: 'model', cellClass: 'py-6', class: 'lightgray mainText--text', sortable: true },
        { text: 'Devices', value: 'originalQuantity', class: 'lightgray mainText--text', sortable: true },
        { text: 'Current License Type', value: 'current_license_type', class: 'lightgray mainText--text', sortable: true },
        { text: 'License Edition', value: 'selectedLicenseEdition', cellClass: 'wth-250', class: 'lightgray mainText--text', sortable: false },
        { text: 'Duration', value: 'selectedDuration', class: 'lightgray mainText--text', sortable: false },
        { text: 'Quantity', value: 'quantity', class: 'lightgray mainText--text', sortable: false },
        { text: 'License', value: 'selectedLicense', class: 'lightgray mainText--text', sortable: false },
        { text: 'Price HT', value: 'price', class: 'lightgray mainText--text', sortable: false },
        { text: 'Price Total', value: 'totalPrice', class: 'lightgray mainText--text', sortable: false },
        { text: 'Actions', value: 'action', class: 'lightgray mainText--text', sortable: false },
      ],
      organizationDetails: {},
      data: [],
      util: util,
      currentPage: 1,
      totalRecords: 0,
      rowsPerPage: 200,
      sortKey: '',
      isSortDesc: false,
      isDifferenceColumnSort: false,
      formValid: true,
      organizationAddressInfo: {
        name: '',
        email: '',
        hqAddress: '',
        unit: '',
        city: '',
        country: '',
        state: '',
        zipCode: '',
        saveForFuture: true,
      },
      agreedToDisclaimer: false,
      errors: {
        agreedToDisclaimer: null,
        name: null,
        email: null,
        hqAddress: null,
        city: null,
        country: null,
        state: null,
        zipCode: null,
      },
      urlError: null,
    }
  },

  computed: {
    showCotermDurationWarning: function() {
      if(this.organizationDetails.licensing_model === 'Per-Device') {
        return false;
      }

      // If all the duration options selected are not equal
      return !this.data.every(el => !el.selectedLicense || el.selectedDuration === this.data?.[0]?.selectedDuration);
    },
  },

  mounted() {
    this.getOrgDetails();
    this.loadData();
    this.loadSavedAddressInfo();
  },

  methods: {
    ...mapActions([
      'setLoading',
      'setLoadingSpinnerText',
      'setSnackbarMessage',
      'trackEvent',
    ]),

    getTotal() {
      return this.data.reduce((prev, curr) => (prev + (curr.quantity*curr.price)), 0);
    },

    removeItemFromCart(item) {
      const index = this.data.map(el => el.model).indexOf(item.model);
      this.data.splice(index, 1);
    },

    handleLicenseEditionChange(selectedLicenseEdition, item) {
      this.data = this.data.map(el => {
        if (el.model === item.model) {
          let durationOptions = this.getDurationOptions(selectedLicenseEdition, el.originalDurationOptions, el.valid_license_combinations);
          let selectedDuration = el.selectedDuration;
          // If the current duration is not available with the new license edition selected
          if (durationOptions.find(d => d.value === el.selectedDuration).disabled) {
            selectedDuration = durationOptions.find(d => !d.disabled)?.value;
          }

          let newSelectedLicense = el.valid_license_combinations.find(c => (c.license_edition === selectedLicenseEdition && c.duration === selectedDuration));

          return {
            ...el,
            durationOptions,
            selectedDuration,
            selectedLicenseEdition,
            price: newSelectedLicense?.price,
            selectedLicense: newSelectedLicense?.title,
            selectedLicenseVariationId: newSelectedLicense?.variation_id,
          }
        } else return el;
      })
    },

    handleDurationChange(selectedDuration, item) {
      this.data = this.data.map(el => {
        if (el.model === item.model) {
          let newSelectedLicense = el.valid_license_combinations.find(c => (c.license_edition === item.selectedLicenseEdition && c.duration === selectedDuration));

          return {
            ...el,
            selectedDuration,
            price: newSelectedLicense?.price,
            selectedLicense: newSelectedLicense?.title,
            selectedLicenseVariationId: newSelectedLicense?.variation_id,
          }
        } else return el;
      })
    },

    setRowsPerPage(rowsPerPage) {
      this.rowsPerPage = rowsPerPage

      this.loadData(this.currentPage)
    },

    getQtyRules(item) {
      return [
        v => !!v || 'Quantity is required',
        v => ( v && v > 0 ) || 'Quantity must be greater than 0',
        v => ( v && v <= item.originalQuantity ) || `Max: ${item.originalQuantity}`,
      ]
    },

    getDurationOptions(selectedLicenseEdition, durationOptions, validLicenseCombinations) {
      return durationOptions.map(option => ({
        text: this.util.formatSymbol(option, 'year'),
        value: option,
        disabled: validLicenseCombinations.filter(el => el.license_edition === selectedLicenseEdition && el.duration === option).length === 0,
      }))
    },

    async getOrgDetails() {
      try {
        this.organizationDetails = await sendGet(`/cart/organization/${this.orgId}`, {});
      } catch (error) {
        this.organizationDetails = {};
      }
    },

    async loadData(currentPage = 1) {
      this.setLoadingSpinnerText('Loading Data...');
      this.setLoading(true);

      const payload = {
        limit: this.rowsPerPage,
        offset: (currentPage - 1) * this.rowsPerPage,
        expired_only: this.expiredOnly,
      };

      try {
        let response;
        response = await sendPost(`/cart/cart-lines/by-organization-devices/${this.orgId}`, {
          devices: this.deviceIds ?? [],
        }, { params: payload });
        this.totalRecords = response.count;
        let selectedLicenseOption;
        this.data = response.results.map(item => {
          selectedLicenseOption = item.valid_license_combinations?.[0];
          return {
            ...item,
            selectedLicense: selectedLicenseOption?.title ?? null,
            selectedLicenseVariationId: selectedLicenseOption?.variation_id,
            selectedLicenseEdition: selectedLicenseOption?.license_edition ?? null,
            selectedDuration: selectedLicenseOption?.duration ?? null,
            price: selectedLicenseOption?.price ?? 0,
            originalQuantity: item.quantity,
            originalLicenseEditionOptions: item.license_edition_options,
            licenseEditionOptions: item.license_edition_options.map(option => ({
              text: option === '' ? 'Default' : option,
              value: option,
              disabled: false,
            })),
            originalDurationOptions: item.duration_options,
            durationOptions: this.getDurationOptions(selectedLicenseOption?.license_edition, item.duration_options, item.valid_license_combinations),
          }
        });
        this.data.sort((a, b) => {
          if (b.valid_license_combinations.length - a.valid_license_combinations.length !== 0) {
            return b.valid_license_combinations.length - a.valid_license_combinations.length;
          }
          return b.originalQuantity - a.originalQuantity;
        });
      } catch (error) {
        this.data = [];
        this.totalRecords = 0;
        this.currentPage = 1;
      }
      this.setLoading(false);
      this.isDifferenceColumnSort = false;
    },

    async loadSavedAddressInfo() {
      try {
        const response = await sendGet(`cart/saved-address-info`, {});
        const addressInfo = response?.results;
        this.organizationAddressInfo = {
          ...this.organizationAddressInfo,
          name: addressInfo?.company ?? '',
          email: addressInfo?.email ?? '',
          hqAddress: addressInfo?.headquarters?.address_line1 ?? '',
          unit: addressInfo?.headquarters?.address_line2 ?? '',
          city: addressInfo?.headquarters?.locality ?? '',
          country: addressInfo?.headquarters?.country_code ?? '',
          state: addressInfo?.headquarters?.administrative_area ?? '',
          zipCode: addressInfo?.headquarters?.postal_code ?? '',
        }
      } catch (error) {}
    },

    sortBy(sortKeys) {
      this.sortKey = sortKeys[0]
      this.isSortDesc = false
      this.isDifferenceColumnSort = true
      this.loadData(this.currentPage)
    },

    changeSortDesc(sortKeysDesc) {
      if (this.isDifferenceColumnSort) {
        return
      }

      this.isSortDesc = sortKeysDesc[0]

      this.loadData(this.currentPage)
    },

    verifyFields() {
      const requiredFields = {
        name: 'Organization Name is required',
        email: 'Email is required',
        hqAddress: 'Street Address is required',
        city: 'City is required',
        country: 'Country is required',
        state: 'State is required',
        zipCode: 'Zip Code is required',
      }

      this.errors.agreedToDisclaimer = !this.agreedToDisclaimer ? ['Please agree before proceeding'] : null;
      Object.keys(requiredFields).forEach(key => {
        this.errors[key] = !this.organizationAddressInfo[key] || this.organizationAddressInfo[key] === '' ? [requiredFields[key]] : null;
      });

      return Object.values(this.errors).every(el => el === null);
    },

    async continueToBillingInformation() {
      if(!this.verifyFields()) {
        return;
      }

      this.urlError = null;
      this.setLoadingSpinnerText('Loading the billing page...');
      this.setLoading(true);

      const { unit, city, state, country, email } = this.organizationAddressInfo;

      const licenses = this.data.filter(el => !!el.selectedLicenseVariationId && el.quantity > 0).map(el => ({
        'variation_id': el.selectedLicenseVariationId,
        'quantity': el.quantity,
      }));

      const data = {
        licenses,
        address: {
          email,
          unit,
          city,
          state,
          country,
          zip_code: this.organizationAddressInfo.zipCode,
          organization: this.organizationAddressInfo.name,
          street_address: this.organizationAddressInfo.hqAddress,
        },
        save_for_future: this.organizationAddressInfo.saveForFuture,
      };

      try {
        let response;
        response = await sendPost(`/cart/cart-link`, data);
        const url = response.results?.cart_url;

        if (url.startsWith('http')) {
          window.location.replace(url);
        } else {
          this.urlError = 'Something went wrong. Please try again or contact support.'
        }
      } catch (error) {
        this.urlError = 'Something went wrong. Please try again or contact support.'
      }
      this.setLoading(false);
      this.isDifferenceColumnSort = false;

      this.trackEvent({
        eventName: 'RedirectedToRhinoNetworksForCheckout',
        eventDetails: this.organizationDetails,
      })
    }
  }
}
</script>

<style lang="scss">
  .lightgray {
    background-color: #e6e6e6 !important;
  }

  .license-cart {
    .wth-250 {
      width: 250px;
      max-width: 250px;
      min-width: 250px;
    }

    .v-data-table {
      .v-chip {
        font-size: 11px;
        width: 120px;
        &__content {
          width: 100%;
          justify-content: center;
        }
      }
    }
  }

  .v-messages__message {
    color: red;
  }

  .theme--dark.v-sheet {
    background-color: #ff5252;
  }
</style>
